import { useEffect, useState, type FormEvent } from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getStripe } from "./stripe-promise";

export function PaymentMethodSetupDialog({
  clientSecret,
  unitName,
  returnUrl,
  onOpenChange,
  onConfirmed,
}: {
  clientSecret: string | null;
  unitName: string;
  returnUrl: string;
  onOpenChange: (open: boolean) => void;
  onConfirmed: (setupIntentId: string) => Promise<void>;
}) {
  const [stripeLoadFailed, setStripeLoadFailed] = useState(false);

  // Stripe.js loads only once a payment form is actually requested. If it
  // cannot load, show a retryable message instead of a form that never works.
  useEffect(() => {
    if (clientSecret === null) {
      return;
    }
    let isCurrent = true;
    getStripe().then((stripe) => {
      if (isCurrent) {
        setStripeLoadFailed(stripe === null);
      }
    });
    return () => {
      isCurrent = false;
    };
  }, [clientSecret]);

  return (
    <Dialog open={clientSecret !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Save a payment method</DialogTitle>
          <DialogDescription>
            By saving this card or bank account you authorize Saile to charge it
            for invoices issued to {unitName}, including when you are not
            present.
          </DialogDescription>
        </DialogHeader>
        {clientSecret && stripeLoadFailed && (
          <p role="alert" className="text-sm text-destructive">
            The payment form could not load. Check your connection, disable any
            content blockers for this site, and try again.
          </p>
        )}
        {clientSecret && !stripeLoadFailed && (
          <Elements
            key={clientSecret}
            stripe={getStripe()}
            options={{ clientSecret }}
          >
            <SetupForm
              returnUrl={returnUrl}
              onCancel={() => onOpenChange(false)}
              onConfirmed={onConfirmed}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SetupForm({
  returnUrl,
  onCancel,
  onConfirmed,
}: {
  returnUrl: string;
  onCancel: () => void;
  onConfirmed: (setupIntentId: string) => Promise<void>;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: "if_required",
      });
      if (error) {
        setErrorMessage(
          error.message ?? "The payment method could not be saved.",
        );
        return;
      }
      if (setupIntent?.status === "succeeded") {
        try {
          await onConfirmed(setupIntent.id);
        } catch {
          // onConfirmed's mutation already shows an error toast via its
          // onError handler. Absorb the rejection here to prevent an
          // unhandled promise rejection and to leave the dialog usable.
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {errorMessage && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!stripe || !elements || isSubmitting}>
          {isSubmitting ? "Saving..." : "Save payment method"}
        </Button>
      </DialogFooter>
    </form>
  );
}
